// Lesson 1.3, Prompts that work.
// Approved by Leena 2026-10-09, converted mechanically from the draft.

export default {
  key: '1-3',
  title: 'Prompts that work',
  minutes: 18,
  status: 'ready',
  outcome: 'Role, context, task and format. Showing an example. Iterating instead of starting over.',

  why: 'Most people type a question, get something vague and generic back, and quietly conclude the tool is overrated. The tool was fine. It was asked a vague question, so it averaged out a vague answer. Four things fix almost all of it, and none of them are clever.',

  blocks: [
    {
      type: 'concept',
      heading: 'Four parts, in any order',
      body: [
        { type: 'deflist', items: [
          { term: 'Role', text: 'Who should it be while answering. "You are a careful proofreader" produces different work from "you are a marketer."' },
          { term: 'Context', text: 'What it needs to know that it cannot guess. Who the reader is, what happened before, what the constraint is.' },
          { term: 'Task', text: 'One clear verb. Summarise. Rewrite. Draft. Compare. Find the gaps. Not three tasks bundled into a paragraph.' },
          { term: 'Format', text: 'What you want back. Three bullets. An email under 150 words. A table with these columns. If you do not say, you get an essay.' },
        ] },
        { type: 'p', text: 'Compare these two. Same request underneath.' },
        { type: 'callout', tone: 'info', text: 'Lazy: "Write an email about the delay."' },
        { type: 'callout', tone: 'info', text: 'Worked: "You are me, writing to a client I have a good relationship with. Our install slipped by a week because a part is late. I have not told them yet and they have a trade show on the 20th. Draft an email under 120 words that owns the delay, gives the new date, and offers one thing that helps with the trade show. Warm, not grovelling."' },

        { type: 'sub', text: 'Show it one example' },
        { type: 'p', text: 'If you have an example of the thing you want, paste it. "Here is one I wrote before. Match this tone and length." One example is worth three paragraphs of describing what you want, and it is the single fastest upgrade to your output.' },

        { type: 'sub', text: 'Iterate, do not restart' },
        { type: 'p', text: 'When the answer is close but wrong, people tend to delete everything and write a new prompt. Do not. Say what is wrong with the one you got: "too formal", "the second paragraph is the important bit, lead with it", "cut it in half". It keeps everything it already understood and fixes only what you named.' },
        { type: 'rule', text: 'Bad output is usually a missing sentence, not a broken tool.' },
      ],
    },

    {
      type: 'exercise',
      heading: 'Fix one of your own',
      steps: [
        { id: 'pick_task', text: 'Pick a real task from this week that you would normally just type as a one-liner.' },
        { id: 'lazy_version', text: 'Write it the lazy way. Send it. Keep the answer.' },
        { id: 'worked_version', text: 'Rewrite it with all four parts, and paste in an example if you have one. Send that.' },
        { id: 'compare', text: 'Compare the two. Tick the box when you have done it, because this is the comparison that makes the rest of the course worth doing.' },
      ],
    },

    {
      type: 'quickcheck',
      questions: [
        {
          q: 'Your draft came back close but far too formal. What is the better move?',
          options: ['Start a new chat with a better prompt', 'Reply "too formal, make it sound like a note to someone I know"'],
          answer: 1,
          because: 'Starting over throws away everything it already understood about your situation.',
        },
        {
          q: 'Which part is missing from "Summarise this for the board"?',
          options: ['Role', 'Format', 'Task'],
          answer: 1,
          because: 'There is a role and a task, but nothing about length or shape, so you will get whatever it feels like. "Five bullets, one line each" changes the answer entirely.',
        },
        {
          q: 'What is the fastest single upgrade to a prompt you use often?',
          options: ['Being more polite', 'Making it longer', 'Pasting in one example of what good looks like'],
          answer: 2,
          because: 'One example beats describing what you want. It is also the easiest thing to reuse.',
        },
      ],
    },

    {
      type: 'promptcard',
      title: 'The four-part skeleton',
      body: 'You are [role]. Here is what you need to know: [context]. Your task is to [one clear verb and what]. Give it back as [format]. Here is an example of the tone I want: [paste something you wrote].',
      note: 'Keep this one and fill the brackets. It is the skeleton under every other prompt in the course.',
    },

    {
      type: 'takeaway',
      text: 'Role, context, task, format, and one example. Then correct what came back instead of starting again.',
    },
  ],
};
